import React, { useCallback, useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Slide1Opening } from '../components/Slide1Opening';
import { Slide2WhyThisMatters } from '../components/Slide2WhyThisMatters';
import { Slide3Bottleneck } from '../components/Slide3Bottleneck';
import { Slide4Funnel } from '../components/Slide4Funnel';
import { Slide5Menu } from '../components/Slide5Menu';
import { Slide6Pricing } from '../components/Slide6Pricing';
import { Slide7FirstPilot } from '../components/Slide7FirstPilot';
import { Slide8Outputs } from '../components/Slide8Outputs';
import { Slide9AnnualCycle } from '../components/Slide9AnnualCycle';
import { Slide9Agencies } from '../components/Slide9Agencies';
import { Slide10Deployment } from '../components/Slide10Deployment';
import { Slide11Decision } from '../components/Slide11Decision';
import { Slide12NextSteps } from '../components/Slide12NextSteps';
import { useScreenInit } from '../useScreenInit.js';
const slides = [Slide1Opening, Slide2WhyThisMatters, Slide3Bottleneck, Slide4Funnel, Slide5Menu, Slide6Pricing, Slide7FirstPilot, Slide8Outputs, Slide9AnnualCycle, Slide9Agencies, Slide10Deployment, Slide11Decision, Slide12NextSteps];
export function Presentation() {
  const screenInit = useScreenInit();
  const [currentSlide, setCurrentSlide] = useState(typeof screenInit?.currentSlide === 'number' ? screenInit.currentSlide : 0);
  const [direction, setDirection] = useState(0);
  const nextSlide = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const CurrentSlideComponent = slides[currentSlide];
  return <div className="relative w-full h-screen overflow-hidden bg-black" data-id="element-1059">
      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-1060">
        <motion.div key={currentSlide} custom={direction} variants={{
        enter: (direction: number) => ({
          x: direction > 0 ? 1000 : -1000,
          opacity: 0
        }),
        center: {
          zIndex: 1,
          x: 0,
          opacity: 1
        },
        exit: (direction: number) => ({
          zIndex: 0,
          x: direction < 0 ? 1000 : -1000,
          opacity: 0
        })
      }} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-1061">
          <CurrentSlideComponent data-id="element-1062" />
        </motion.div>
      </AnimatePresence>

      {/* Logos — persistent across all slides */}
      <div className="absolute top-5 right-20 z-50 flex items-center gap-3" data-id="element-1063">
        <div className="bg-white rounded-full px-3 py-1.5 shadow-md" data-id="element-1064">
          <img src="https://cdn.magicpatterns.com/uploads/6UmWqd3TWdNZzkJ6AZQjM5/image.png" alt="Snakker logo" className="h-5 w-auto object-contain" data-id="element-1065" />
        </div>
        <div className="bg-white rounded-full px-3 py-1.5 shadow-md" data-id="element-1066">
          <img src="https://cdn.magicpatterns.com/uploads/1yzsnj6wqDNUHRV1oyVyfh/Screenshot_2026-03-24_at_08.34.05.png" alt="Takeda logo" className="h-6 w-auto object-contain" data-id="element-1067" />
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="absolute bottom-6 left-0 w-full px-8 flex justify-between items-center z-50 pointer-events-none" data-id="element-1068">
        <button onClick={prevSlide} disabled={currentSlide === 0} className={`
            pointer-events-auto p-2 rounded-full bg-black/10 hover:bg-black/20 text-gray-500 hover:text-gray-900 transition-all
            ${currentSlide === 0 ? 'opacity-0' : 'opacity-100'}
          `} aria-label="Previous slide" data-id="element-1069">
          <ChevronLeft className="w-8 h-8" data-id="element-1070" />
        </button>

        <span className="text-sm font-medium text-gray-400 bg-white/80 px-3 py-1 rounded-full backdrop-blur-sm shadow-sm" data-id="element-1071">
          {currentSlide + 1} / {slides.length}
        </span>

        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className={`
            pointer-events-auto p-2 rounded-full bg-black/10 hover:bg-black/20 text-gray-500 hover:text-gray-900 transition-all
            ${currentSlide === slides.length - 1 ? 'opacity-0' : 'opacity-100'}
          `} aria-label="Next slide" data-id="element-1072">
          <ChevronRight className="w-8 h-8" data-id="element-1073" />
        </button>
      </div>
    </div>;
}